import { apiClient, ApiResponse } from './api-client'

export interface TaskAttachment {
  id: number
  taskId: number
  name: string
  filePath: string
  size: number
  type: string
  uploadedAt: string
  uploadedBy: string
  uploaderName: string
}

export interface UploadResponse {
  attachment?: TaskAttachment
  attachments?: TaskAttachment[]
  errors?: string[]
}

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024
const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/zip',
  'application/x-rar-compressed'
])

class AttachmentService {
  private baseUrl = '/api'

  private validateFile(file: File): void {
    if (!file || file.size <= 0) {
      throw new Error('Arquivo vazio ou inválido')
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      throw new Error('Arquivo muito grande. Máximo: 10MB')
    }

    if (!ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)) {
      throw new Error('Tipo de arquivo não permitido')
    }
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  private async parseFetchResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
    const payload = await response.json().catch(() => null) as ApiResponse<T> | null

    if (!response.ok) {
      throw new Error(payload?.message || fallbackMessage)
    }

    if (!payload?.success) {
      throw new Error(payload?.message || fallbackMessage)
    }

    if (payload.data === undefined || payload.data === null) {
      throw new Error('Resposta da API sem dados')
    }

    return payload.data
  }

  async uploadTaskAttachment(taskId: string, file: File): Promise<TaskAttachment> {
    this.validateFile(file)

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${this.baseUrl}/tasks/${taskId}/attachments`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: formData
    })

    return this.parseFetchResponse<TaskAttachment>(response, 'Erro ao enviar arquivo')
  }

  async uploadMultipleTaskAttachments(taskId: string, files: FileList): Promise<UploadResponse> {
    const selectedFiles = Array.from(files)
    selectedFiles.forEach(file => this.validateFile(file))

    const formData = new FormData()
    selectedFiles.forEach(file => {
      formData.append('files', file)
    })

    const response = await fetch(`${this.baseUrl}/tasks/${taskId}/attachments/multiple`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: formData
    })

    return this.parseFetchResponse<UploadResponse>(response, 'Erro ao enviar arquivos')
  }

  async getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
    const response = await apiClient.get<ApiResponse<TaskAttachment[]>>(
      `${this.baseUrl}/tasks/${taskId}/attachments`
    )

    return response.data.data || []
  }

  async downloadAttachment(attachmentId: number): Promise<Blob> {
    const response = await apiClient.get(
      `${this.baseUrl}/attachments/${attachmentId}/download`,
      {
        responseType: 'blob'
      }
    )

    return response.data
  }

  async deleteAttachment(attachmentId: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/attachments/${attachmentId}`)
  }

  async getUserAttachments(): Promise<TaskAttachment[]> {
    const response = await apiClient.get<ApiResponse<TaskAttachment[]>>(
      `${this.baseUrl}/attachments/my`
    )

    return response.data.data || []
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  getFileIcon(type: string): string {
    if (type.includes('image/')) return '🖼️'
    if (type.includes('pdf')) return '📄'
    if (type.includes('word') || type.includes('document')) return '📝'
    if (type.includes('sheet') || type.includes('excel')) return '📊'
    if (type.includes('zip') || type.includes('rar')) return '🗜️'
    if (type.includes('text/')) return '📃'
    return '📁'
  }

  isImageFile(type: string): boolean {
    return type.startsWith('image/')
  }

  async downloadAndSaveFile(attachmentId: number, fileName: string): Promise<void> {
    try {
      const blob = await this.downloadAttachment(attachmentId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.setTimeout(() => window.URL.revokeObjectURL(url), 0)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Erro ao baixar arquivo')
    }
  }
}

export const attachmentService = new AttachmentService()
